import React, { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { StoryElement } from "@quintype/components";

/**
 * DatawrapperEmbed Component
 * --------------------------
 * - Decodes Datawrapper base64 embed-js
 * - Extracts iframe and derives static PNG for print
 * - Handles print page breaks cleanly
 * - Does not depend on element.metadata.url
 */

// Single global listener — safe to call multiple times, only registers once
let datawrapperListenerRegistered = false;

const registerDatawrapperListener = () => {
  if (datawrapperListenerRegistered || typeof window === "undefined") return;
  datawrapperListenerRegistered = true;
  window.addEventListener("message", (a) => {
    if (typeof a.data["datawrapper-height"] !== "undefined") {
      const iframes = document.querySelectorAll("iframe");
      for (const t in a.data["datawrapper-height"]) {
        for (let i = 0; i < iframes.length; i++) {
          if (iframes[i].contentWindow === a.source) {
            iframes[i].style.height = a.data["datawrapper-height"][t] + "px";
          }
        }
      }
    }
  });
};

const DatawrapperEmbed = ({ element, story }) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    registerDatawrapperListener();
  }, []);

  // --- Decode the base64 embed-js safely ---
  const decodedEmbedJs = useMemo(() => {
    try {
      if (element["embed-js"]) {
        if (typeof window !== "undefined") {
          return atob(element["embed-js"]);
        }
        return Buffer.from(element["embed-js"], "base64").toString("utf-8");
      }
    } catch (err) {
      console.error("Error decoding Datawrapper embed-js:", err);
    }
    return "";
  }, [element]);

  // --- Extract iframe HTML, src, chart ID, and image URL ---
  const { iframeHtml, chartUrl, imageUrl } = useMemo(() => {
    if (!decodedEmbedJs) return { iframeHtml: "", chartUrl: "", imageUrl: "" };

    // Find iframe tag
    const iframeMatch = decodedEmbedJs.match(/<iframe[^>]+src=["']([^"']+)["'][^>]*>/i);
    const src = iframeMatch ? iframeMatch[1] : "";

    let chartUrl = "";
    let imageUrl = "";

    if (src) {
      chartUrl = src; // use the same as iframe src for "View chart online"
      const idMatch = src.match(/dwcdn\.net\/([A-Za-z0-9]+)\//);
      if (idMatch) {
        imageUrl = `https://datawrapper.dwcdn.net/${idMatch[1]}/full.png`;
      }
    }

    return {
      iframeHtml: iframeMatch ? iframeMatch[0] : decodedEmbedJs,
      chartUrl,
      imageUrl,
    };
  }, [decodedEmbedJs]);

  return (
    <div className="datawrapper-embed-container avoid-break">
      {/* Interactive iframe for screen */}
      {isMounted && iframeHtml && (
        <div
          className="datawrapper-iframe-wrapper"
          dangerouslySetInnerHTML={{ __html: iframeHtml }}
        />
      )}

      {/* Fallback (if embed decoding fails) */}
      {isMounted && !iframeHtml && (
        <StoryElement element={element} story={story} />
      )}

      {/* Static PNG fallback for print */}
      {imageUrl && (
        <div className="datawrapper-print-fallback print-only">
          <img
            src={imageUrl}
            alt="Datawrapper Chart"
            className="datawrapper-fallback-image"
          />
          {chartUrl && (
            <p className="datawrapper-link">
              View interactive version:{" "}
              <a href={chartUrl} target="_blank" rel="noopener noreferrer">
                {chartUrl}
              </a>
            </p>
          )}
        </div>
      )}
    </div>
  );
};

DatawrapperEmbed.propTypes = {
  element: PropTypes.object.isRequired,
  story: PropTypes.object.isRequired,
};

export default DatawrapperEmbed;